import type { HarvestLog } from '../types/production'

import { ensureOfflineReady } from '../offline/initOffline'
import { getActiveTenantKey } from '../offline/tenant'
import { enqueueEntityChange, enqueueEntityChanges } from '../offline/outbox/entityOutbox'
import { getOfflineDb } from '../offline/dbFactory'
import {
  dexieHarvestLogRepo,
  type HarvestLogListParams
} from '../offline/repos/DexieHarvestLogRepo'
import { normalizeToIsoZ } from './dateNormalization'
import {
  buildQueuedEntityMutationResult,
  readEntityBatchSyncStatus,
  retryEntityBatchSyncStatusWithReport,
  type EntityBatchSyncRetryReport,
  type EntityBatchSyncStatus,
  type EntityMutationResult
} from './entityMutationSync'
import { createId } from './id'
import { api } from './api'

const nowIso = (): string => new Date().toISOString()

const toOptionalTrimmed = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed || undefined
}

const normalizeHarvestKg = (value: unknown): string => {
  const raw = typeof value === 'number'
    ? value
    : Number(String(value ?? '').replaceAll(',', '.').trim())
  if (!Number.isFinite(raw)) {
    throw new Error('Harvest kg must be numeric')
  }
  if (raw < 0) {
    throw new Error('Harvest kg must be zero or greater')
  }
  if (raw > 10_000_000) {
    throw new Error('Harvest kg must be 10,000,000 or below')
  }
  // Store as a decimal string with up to 3 fraction digits so the client-
  // side value round-trips through the Prisma `@db.Decimal(12, 3)` column
  // without precision drift.
  return raw.toFixed(3)
}

const normalizeHarvestCount = (value: unknown): number | undefined => {
  if (value === undefined || value === null || value === '') return undefined
  const raw = typeof value === 'number' ? value : Number(String(value).replaceAll(',', '').trim())
  if (!Number.isFinite(raw)) {
    throw new Error('Harvest count must be numeric')
  }
  if (!Number.isInteger(raw)) {
    throw new Error('Harvest count must be a whole number')
  }
  if (raw < 0) {
    throw new Error('Harvest count must be zero or greater')
  }
  if (raw > 10_000_000) {
    throw new Error('Harvest count must be 10,000,000 or below')
  }
  return raw
}

const normalizeMethod = (value: unknown): 'count' | 'estimation' => {
  if (value === 'count' || value === 'estimation') return value
  throw new Error("Method must be 'count' or 'estimation'")
}

const normalizeRecordedAt = (raw?: string): string => {
  if (!raw) return nowIso()
  const normalized = normalizeToIsoZ(raw)
  if (normalized.ok) return normalized.isoZ
  return nowIso()
}

const isOnline = (): boolean => {
  if (typeof navigator === 'undefined') return true
  return navigator.onLine
}

const REMOTE_LIST_TTL_MS = 30_000
const lastRemoteListAtByKey = new Map<string, number>()

const buildRemoteListKey = (params: HarvestLogListParams): string => {
  const clean = {
    tenantKey: getActiveTenantKey(),
    farmId: params.farmId?.trim() || '',
    productionUnitId: params.productionUnitId?.trim() || '',
    lot: params.lot?.trim() || '',
    lotId: params.lotId?.trim() || '',
    productionRowId: params.productionRowId?.trim() || '',
    fromRecordedAt: params.fromRecordedAt?.trim() || '',
    toRecordedAt: params.toRecordedAt?.trim() || ''
  }
  return JSON.stringify(clean)
}

const shouldAttemptRemoteList = (params: HarvestLogListParams): boolean => {
  if (!isOnline()) return false
  const key = buildRemoteListKey(params)
  const lastAt = lastRemoteListAtByKey.get(key)
  if (!lastAt) return true
  return Date.now() - lastAt > REMOTE_LIST_TTL_MS
}

const getPendingHarvestLogIds = async (): Promise<Set<string>> => {
  const entries = await getOfflineDb().entityOutbox.toArray()
  return new Set(
    entries
      .filter((entry) => entry.entityType === 'harvestLog')
      .map((entry) => entry.entityId)
  )
}

const hasPendingHarvestLogOutboxEntry = async (entityId: string): Promise<boolean> => {
  const trimmed = entityId.trim()
  if (!trimmed) return false

  try {
    const db = getOfflineDb()
    const match = await db.entityOutbox
      .where('[entityType+entityId]')
      .equals(['harvestLog', trimmed])
      .first()
    return Boolean(match)
  } catch {
    return false
  }
}

const pruneRemovedRemoteHarvestLogs = async (
  cached: HarvestLog[],
  remoteMapped: HarvestLog[]
): Promise<void> => {
  const remoteIds = new Set(remoteMapped.map((log) => log.id))

  for (const log of cached) {
    if (remoteIds.has(log.id)) continue
    // Preserve local-only changes until their outbox entry is flushed.
    // eslint-disable-next-line no-await-in-loop
    const hasPending = await hasPendingHarvestLogOutboxEntry(log.id)
    if (hasPending) continue
    // eslint-disable-next-line no-await-in-loop
    await dexieHarvestLogRepo.delete(log.id)
  }
}

export const normalizeRemoteHarvestLog = (candidate: any): HarvestLog | null => {
  if (!candidate || typeof candidate !== 'object') return null
  const id = typeof candidate.id === 'string' ? candidate.id.trim() : ''
  const farmId = typeof candidate.farmId === 'string' ? candidate.farmId.trim() : ''
  const productionUnitId =
    typeof candidate.productionUnitId === 'string' ? candidate.productionUnitId.trim() : ''
  const recordedAtRaw = typeof candidate.recordedAt === 'string' ? candidate.recordedAt.trim() : ''
  if (!id || !farmId || !productionUnitId || !recordedAtRaw) return null

  try {
    return {
      id,
      farmId,
      productionUnitId,
      lot: typeof candidate.lot === 'string' ? candidate.lot.trim() || undefined : undefined,
      lotId: typeof candidate.lotId === 'string' ? candidate.lotId.trim() || undefined : undefined,
      productionRowId:
        typeof candidate.productionRowId === 'string' ? candidate.productionRowId.trim() || undefined : undefined,
      recordedAt: normalizeRecordedAt(recordedAtRaw),
      harvestKg: normalizeHarvestKg(candidate.harvestKg),
      harvestCount: normalizeHarvestCount(candidate.harvestCount),
      method: normalizeMethod(candidate.method),
      notes: toOptionalTrimmed(candidate.notes),
      metadata:
        typeof candidate.metadata === 'object' && candidate.metadata !== null ? candidate.metadata : undefined,
      createdAt: typeof candidate.createdAt === 'string' ? candidate.createdAt : undefined,
      updatedAt: typeof candidate.updatedAt === 'string' ? candidate.updatedAt : undefined
    }
  } catch {
    return null
  }
}

export type HarvestLogCreatePayload = {
  id?: string
  farmId: string
  productionUnitId: string
  lot?: string
  lotId?: string
  productionRowId?: string
  recordedAt?: string
  harvestKg: number | string
  harvestCount?: number | string
  method: 'count' | 'estimation'
  notes?: string
  metadata?: Record<string, unknown>
}

export type HarvestLogUpdatePayload = Partial<{
  lot: string | null
  lotId: string | null
  productionRowId: string | null
  recordedAt: string
  harvestKg: number | string
  harvestCount: number | string | null
  method: 'count' | 'estimation'
  notes: string | null
  metadata: Record<string, unknown> | null
}>

export type HarvestLogMutationResult = EntityMutationResult<HarvestLog>
export type HarvestLogDeleteResult = EntityMutationResult<void>

const normalizeHarvestLogRecord = (args: {
  candidate: HarvestLogCreatePayload | HarvestLog
  id?: string
  createdAt?: string
  updatedAt?: string
}): HarvestLog => {
  const farmId = toOptionalTrimmed(args.candidate.farmId)
  if (!farmId) throw new Error('Facility is required')

  const productionUnitId = toOptionalTrimmed(args.candidate.productionUnitId)
  if (!productionUnitId) throw new Error('Production unit is required')

  const timestamp = nowIso()
  const candidateId =
    typeof (args.candidate as HarvestLog).id === 'string'
      ? (args.candidate as HarvestLog).id.trim()
      : ''

  return {
    id: args.id?.trim() || candidateId || createId('hrv'),
    farmId,
    productionUnitId,
    lot: toOptionalTrimmed(args.candidate.lot),
    lotId: toOptionalTrimmed(args.candidate.lotId),
    productionRowId: toOptionalTrimmed(args.candidate.productionRowId),
    recordedAt: normalizeRecordedAt(args.candidate.recordedAt),
    harvestKg: normalizeHarvestKg(args.candidate.harvestKg),
    harvestCount: normalizeHarvestCount(args.candidate.harvestCount),
    method: normalizeMethod(args.candidate.method),
    notes: toOptionalTrimmed(args.candidate.notes),
    metadata: args.candidate.metadata,
    createdAt: args.createdAt?.trim() || timestamp,
    updatedAt: args.updatedAt?.trim() || timestamp
  }
}

export const harvestLogApi = {
  readHarvestLogSyncStatus: async (logIds: string[]): Promise<EntityBatchSyncStatus> => {
    return readEntityBatchSyncStatus('harvestLog', logIds)
  },

  retryHarvestLogSyncStatusWithReport: async (
    logIds: string[]
  ): Promise<EntityBatchSyncRetryReport> => {
    return retryEntityBatchSyncStatusWithReport({
      entityType: 'harvestLog',
      entityIds: logIds
    })
  },

  list: async (
    params: HarvestLogListParams = {},
    options?: { preferLocal?: boolean; forceRemote?: boolean }
  ): Promise<HarvestLog[]> => {
    await ensureOfflineReady()

    const [cached, pendingIds] = await Promise.all([
      dexieHarvestLogRepo.list(params),
      getPendingHarvestLogIds()
    ])
    const shouldFetchRemote = options?.forceRemote ? isOnline() : shouldAttemptRemoteList(params)

    // Same TTL-based pattern as fishStockLogApi / weightSampleLogApi
    // (finding #57): `preferLocal` remains in the signature for back-compat
    // but freshness is decided by the `REMOTE_LIST_TTL_MS` gate so
    // peer-device events surface after the TTL window even when the caller
    // opted in to prefer-local.
    if (!shouldFetchRemote) {
      return cached
    }

    const key = buildRemoteListKey(params)
    try {
      const remote = await api.harvestLogs.list({
        farmId: params.farmId,
        productionUnitId: params.productionUnitId,
        lot: params.lot,
        lotId: params.lotId,
        productionRowId: params.productionRowId,
        fromRecordedAt: params.fromRecordedAt,
        toRecordedAt: params.toRecordedAt,
        noCache: options?.forceRemote
      })

      const mapped = remote
        .map(normalizeRemoteHarvestLog)
        .filter((log): log is HarvestLog => Boolean(log))
        .filter((log) => !pendingIds.has(log.id))

      await pruneRemovedRemoteHarvestLogs(cached, mapped)
      await dexieHarvestLogRepo.upsertMany(mapped)
      lastRemoteListAtByKey.set(key, Date.now())

      return dexieHarvestLogRepo.list(params)
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('[harvestLogApi] remote list failed; falling back to cache', error)
      }
      if (cached.length > 0) return cached
      throw error
    }
  },

  get: async (id: string): Promise<HarvestLog | undefined> => {
    await ensureOfflineReady()
    return dexieHarvestLogRepo.getById(id)
  },

  create: async (payload: HarvestLogCreatePayload): Promise<HarvestLogMutationResult> => {
    await ensureOfflineReady()
    const timestamp = nowIso()
    const log = normalizeHarvestLogRecord({
      candidate: payload,
      id: payload.id,
      createdAt: timestamp,
      updatedAt: timestamp
    })

    await dexieHarvestLogRepo.upsert(log)
    await enqueueEntityChange({
      entityType: 'harvestLog',
      op: 'upsert',
      entityId: log.id,
      payload: {
        ...log,
        id: log.id
      }
    })

    return buildQueuedEntityMutationResult(log, {
      entityType: 'harvestLog',
      entityId: log.id
    })
  },

  saveImportedLogs: async (logs: HarvestLog[]): Promise<HarvestLog[]> => {
    await ensureOfflineReady()
    if (logs.length === 0) return []

    const timestamp = nowIso()
    const normalizedLogs = logs.map((log) =>
      normalizeHarvestLogRecord({
        candidate: log,
        createdAt: typeof log.createdAt === 'string' ? log.createdAt : timestamp,
        updatedAt: timestamp
      })
    )

    await dexieHarvestLogRepo.upsertMany(normalizedLogs)
    await enqueueEntityChanges(
      normalizedLogs.map((log) => ({
        entityType: 'harvestLog' as const,
        op: 'upsert' as const,
        entityId: log.id,
        payload: {
          ...log,
          id: log.id
        }
      }))
    )

    return normalizedLogs
  },

  update: async (
    id: string,
    patch: HarvestLogUpdatePayload
  ): Promise<HarvestLogMutationResult> => {
    await ensureOfflineReady()

    const existing = await dexieHarvestLogRepo.getById(id)
    if (!existing) throw new Error('Harvest log not found')

    const next: HarvestLog = {
      ...existing,
      lot: Object.prototype.hasOwnProperty.call(patch, 'lot')
        ? toOptionalTrimmed(patch.lot ?? undefined)
        : existing.lot,
      lotId: Object.prototype.hasOwnProperty.call(patch, 'lotId')
        ? toOptionalTrimmed(patch.lotId ?? undefined)
        : existing.lotId,
      productionRowId: Object.prototype.hasOwnProperty.call(patch, 'productionRowId')
        ? toOptionalTrimmed(patch.productionRowId ?? undefined)
        : existing.productionRowId,
      recordedAt: patch.recordedAt ? normalizeRecordedAt(patch.recordedAt) : existing.recordedAt,
      harvestKg: Object.prototype.hasOwnProperty.call(patch, 'harvestKg')
        ? normalizeHarvestKg(patch.harvestKg)
        : existing.harvestKg,
      harvestCount: Object.prototype.hasOwnProperty.call(patch, 'harvestCount')
        ? normalizeHarvestCount(patch.harvestCount ?? undefined)
        : existing.harvestCount,
      method: Object.prototype.hasOwnProperty.call(patch, 'method')
        ? normalizeMethod(patch.method)
        : existing.method,
      notes: Object.prototype.hasOwnProperty.call(patch, 'notes')
        ? toOptionalTrimmed(patch.notes ?? undefined)
        : existing.notes,
      metadata: Object.prototype.hasOwnProperty.call(patch, 'metadata')
        ? (patch.metadata ?? undefined)
        : existing.metadata,
      updatedAt: nowIso()
    }

    await dexieHarvestLogRepo.upsert(next)
    await enqueueEntityChange({
      entityType: 'harvestLog',
      op: 'upsert',
      entityId: next.id,
      payload: {
        ...next,
        id: next.id
      }
    })

    return buildQueuedEntityMutationResult(next, {
      entityType: 'harvestLog',
      entityId: next.id
    })
  },

  delete: async (id: string): Promise<HarvestLogDeleteResult> => {
    await ensureOfflineReady()

    const existing = await dexieHarvestLogRepo.getById(id)

    await dexieHarvestLogRepo.delete(id)
    await enqueueEntityChange({
      entityType: 'harvestLog',
      op: 'delete',
      entityId: id,
      payload: existing ? { ...existing } : undefined
    })

    return buildQueuedEntityMutationResult(undefined, {
      entityType: 'harvestLog',
      entityId: id
    })
  }
}
